import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Calendar, Building2, Layers, Lock, Edit3, TrendingUp, ChevronLeft, ChevronRight, LockOpen } from 'lucide-react';
import { useForecastV3, useMonthlyVersions } from '../../context/ForecastV3Context';
import { defaultMonthForYear } from '../../utils';
import { StatusPill } from '../shared/StatusPill';
import { VersionSwitchModal } from '../shared/VersionSwitchModal';
import type { ForecastVersion, MonthVersionInfo, MonthStatus } from '../../types';
import { headerLayoutFor } from './forecastHeaderLayout';
import { HeaderSelect } from './HeaderSelect';
// @ts-ignore - JavaScript module without types
import { useTheme } from '../../../../context/ThemeContext';
import { usePageHeaderSlot } from '../../../../context/NavbarActionsContext';

interface LocationOption {
  id: number;
  name: string;
  orgId: number;
}

interface ForecastHeaderProps {
  /** Classic shell: keep the top-right clear of the floating tray (~150px).
   *  On by default; fullscreen covers the tray and turns it off. */
  reserveTrayGap?: boolean;
  companyName: string;
  propertyName: string;
  versions: (ForecastVersion & { isDefault?: boolean })[];
  monthVersions?: MonthVersionInfo[];
  defaultVersionId?: number | null;
  locations?: LocationOption[];
  selectedLocation?: LocationOption | null;
  onLocationChange?: (location: LocationOption) => void;
  onVersionChange?: (versionId: number) => void;
  onSetDefaultVersion?: (versionId: string) => Promise<void>;
  onMonthSelect?: (month: number) => void;
  onLockMonth?: () => void;
  onUnlockMonth?: (month: number) => void;
  onDateRangeChange?: () => void;
  onBudgetForecastToggle?: (mode: 'budget' | 'forecast') => void;
  /** Ribbon mode: chips only (property · year/month · version · lock), no
   *  bar of its own — the module composes it into one row with the active
   *  page's toolbar. */
  compact?: boolean;
  /** Compact only: move the property + version pickers into the app header's slot when the shell provides one. */
  hoistPickers?: boolean;
}

// Month names for display
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Status icon component
function MonthStatusIcon({ status, size = 12 }: { status: MonthStatus; size?: number }) {
  switch (status) {
    case 'locked':
      return <Lock size={size} style={{ color: 'var(--lodge-onyx-400)' }} />;
    case 'draft':
      return <Edit3 size={size} style={{ color: 'var(--lodge-orange-400)' }} />;
    case 'projected':
      return <TrendingUp size={size} style={{ color: 'var(--color-info)' }} />;
    default:
      return null;
  }
}

// Divider between groups
function HeaderDivider({ hidden = false }: { hidden?: boolean }) {
  if (hidden) return null;
  return <div className="w-px h-6 flex-none" style={{ background: 'var(--lodge-onyx-100)' }} />;
}

/**
 * Width that drives the collapse tier. The full bar measures itself; the
 * compact ribbon header is content-sized, so it measures the ribbon it sits
 * in — the row that actually runs out of room.
 */
function useHeaderLayout<T extends HTMLElement>(measureParent = false) {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState<number>(1400);
  const target = () => (measureParent ? ref.current?.parentElement ?? ref.current : ref.current);

  useLayoutEffect(() => {
    const node = target();
    if (!node) return;
    // Measure once up front so the first paint is already correct — starting
    // wide and correcting on the next frame is a visible reflow.
    setWidth(node.getBoundingClientRect().width);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const node = target();
    if (!node || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(entries => {
      const next = entries[0]?.contentRect?.width;
      if (next) setWidth(next);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return { ref, layout: headerLayoutFor(width) };
}

// Caps once the pickers live in the app header, which has the width to spare.
const SHELL_PROPERTY_MAX = 320;
const SHELL_VERSION_MAX = 280;

export function ForecastHeader({
  companyName,
  propertyName,
  versions,
  monthVersions: propMonthVersions,
  defaultVersionId,
  locations = [],
  selectedLocation,
  onLocationChange,
  onVersionChange,
  onSetDefaultVersion,
  onMonthSelect,
  onLockMonth,
  onUnlockMonth,
  onDateRangeChange,
  onBudgetForecastToggle,
  reserveTrayGap = true,
  compact = false,
  hoistPickers = false,
}: ForecastHeaderProps) {
  const { state, setVersion, setYear, setActiveMonth } = useForecastV3();
  const { insetActive } = useTheme();
  const trayGap = reserveTrayGap && !insetActive;
  // Inset shell: property + version ride the app header beside the
  // breadcrumb, where long names have room; the ribbon keeps the period,
  // lock and the page's working controls.
  const headerSlot = usePageHeaderSlot();
  const pickersInShell = compact && hoistPickers && !!headerSlot;
  const { activeMonth, monthVersions: contextMonthVersions } = useMonthlyVersions();
  const { currentVersion, dateRange, year } = state;

  const monthVersions = propMonthVersions || contextMonthVersions;

  const { ref: headerRef, layout } = useHeaderLayout<HTMLDivElement>(compact);
  const {
    tier, showYearArrows, showLockLabel, showLockMonthName,
    showTypeChip, showStatusPill, showDividers, propertyMaxWidth, versionMaxWidth,
  } = layout;

  // Modal state
  const [isVersionModalOpen, setIsVersionModalOpen] = useState(false);
  const [pendingVersion, setPendingVersion] = useState<ForecastVersion | null>(null);
  const [isSettingDefault, setIsSettingDefault] = useState(false);
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const periodRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!showMonthPicker) return;
    const onDown = (e: MouseEvent) => {
      if (periodRef.current && !periodRef.current.contains(e.target as Node)) setShowMonthPicker(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [showMonthPicker]);

  const handleVersionSelect = (id: number | string) => {
    const versionId = typeof id === 'number' ? id : parseInt(id, 10);
    const version = versions.find((v) => v.id === versionId);
    if (!version || version.id === currentVersion?.id) return;

    if (onSetDefaultVersion) {
      setPendingVersion(version);
      setIsVersionModalOpen(true);
    } else {
      setVersion(version);
      onVersionChange?.(versionId);
    }
  };

  const handleSetAsDefault = async () => {
    if (!pendingVersion || !onSetDefaultVersion) return;
    setIsSettingDefault(true);
    try {
      await onSetDefaultVersion(String(pendingVersion.id));
      setVersion(pendingVersion);
      onVersionChange?.(pendingVersion.id);
      setIsVersionModalOpen(false);
      setPendingVersion(null);
    } catch (error) {
      console.error('Failed to set default version:', error);
    } finally {
      setIsSettingDefault(false);
    }
  };

  const handleViewOnly = () => {
    if (!pendingVersion) return;
    setVersion(pendingVersion);
    onVersionChange?.(pendingVersion.id);
    setIsVersionModalOpen(false);
    setPendingVersion(null);
  };

  const handleCloseModal = () => {
    setIsVersionModalOpen(false);
    setPendingVersion(null);
  };

  const handleLocationSelect = (id: number | string) => {
    const locationId = typeof id === 'number' ? id : parseInt(id, 10);
    const location = locations.find((l) => l.id === locationId);
    if (location) onLocationChange?.(location);
  };

  const handleYearSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newYear = parseInt(e.target.value, 10);
    setYear(newYear);
    setActiveMonth(defaultMonthForYear(newYear));
  };

  const handleMonthClick = (month: number) => {
    setActiveMonth(month);
    onMonthSelect?.(month);
    setShowMonthPicker(false);
  };

  const handleYearNav = (direction: 'prev' | 'next') => {
    const newYear = direction === 'prev' ? year - 1 : year + 1;
    setYear(newYear);
    setActiveMonth(defaultMonthForYear(newYear));
  };

  // Year options
  const currentYear = new Date().getFullYear();
  const yearOptions = [currentYear - 2, currentYear - 1, currentYear, currentYear + 1, currentYear + 2];

  // Current month info
  const currentMonthInfo = activeMonth ? monthVersions.find(mv => mv.month === activeMonth) : null;

  // Get working month
  const getWorkingMonth = (): number => {
    if (activeMonth) return activeMonth;
    const draftMonth = monthVersions.find(mv => mv.status === 'draft');
    if (draftMonth) return draftMonth.month;
    const lockedMonths = monthVersions.filter(mv => mv.status === 'locked');
    if (lockedMonths.length > 0) {
      const lastLocked = Math.max(...lockedMonths.map(mv => mv.month));
      return Math.min(lastLocked + 1, 12);
    }
    return defaultMonthForYear(year);
  };

  const workingMonth = getWorkingMonth();
  const workingMonthStatus = monthVersions.find(mv => mv.month === workingMonth)?.status || 'none';

  // The versions list is filtered to the selected year. If the current version
  // belongs to another year (e.g. opened from the Versions tab), include it so
  // the dropdown shows what is actually being edited instead of silently
  // falling back to the first option.
  const currentVersionInList = !currentVersion || versions.some(v => v.id === currentVersion.id);
  const versionOptions: { id: number; label: string }[] = [
    ...(!currentVersionInList && currentVersion
      ? [{ id: currentVersion.id, label: `${currentVersion.name} (${currentVersion.year})` }]
      : []),
    ...versions.map(v => ({ id: v.id, label: `${v.name}${v.isDefault ? ' ★' : ''}` })),
  ];

  // The lock button reads the same in both layouts.
  const lockButton = (() => {
    if (currentMonthInfo?.status === 'draft' && onLockMonth) {
      return (
        <button
          onClick={onLockMonth}
          className="flex items-center justify-center w-[30px] h-[30px] rounded-lg transition-colors flex-none"
          style={{ color: 'var(--lodge-onyx-600)', background: 'var(--lodge-onyx-50)' }}
          onMouseEnter={e => (e.currentTarget.style.background = 'var(--lodge-onyx-100)')}
          onMouseLeave={e => (e.currentTarget.style.background = 'var(--lodge-onyx-50)')}
          title={`Lock ${MONTH_NAMES[workingMonth - 1]}`}
          aria-label={`Lock ${MONTH_NAMES[workingMonth - 1]}`}
        >
          <Lock size={14} />
        </button>
      );
    }
    if (currentMonthInfo?.status === 'locked' && onUnlockMonth && activeMonth) {
      return (
        <button
          onClick={() => onUnlockMonth(activeMonth)}
          className="flex items-center justify-center w-[30px] h-[30px] rounded-lg transition-colors flex-none"
          style={{ color: 'var(--lodge-onyx-600)', background: 'var(--lodge-onyx-50)' }}
          onMouseEnter={e => (e.currentTarget.style.background = 'var(--lodge-onyx-100)')}
          onMouseLeave={e => (e.currentTarget.style.background = 'var(--lodge-onyx-50)')}
          title={`Unlock ${MONTH_NAMES[activeMonth - 1]}`}
          aria-label={`Unlock ${MONTH_NAMES[activeMonth - 1]}`}
        >
          <LockOpen size={14} />
        </button>
      );
    }
    return null;
  })();

  const monthPicker = showMonthPicker && (
    <div className="absolute top-full mt-1 left-0 rounded-lg z-[9999] p-2 min-w-[260px]" style={{ background: 'var(--lodge-white)', border: '1px solid var(--lodge-onyx-100)', boxShadow: 'var(--shadow-lg)' }}>
      {compact && (
        // Year lives inside the period chip in ribbon mode, so the picker
        // carries the year row the bar used to.
        <div className="flex items-center justify-between mb-2 px-1">
          <button onClick={() => handleYearNav('prev')} className="p-1 rounded" style={{ color: 'var(--lodge-onyx-400)' }} aria-label="Previous year"><ChevronLeft size={14} /></button>
          <select
            value={year}
            onChange={handleYearSelect}
            className="text-sm bg-transparent !border-0 focus:outline-none !ring-0 !shadow-none cursor-pointer font-semibold appearance-none text-center"
            style={{ color: 'var(--lodge-onyx-800)' }}
            aria-label="Year"
          >
            {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <button onClick={() => handleYearNav('next')} className="p-1 rounded" style={{ color: 'var(--lodge-onyx-400)' }} aria-label="Next year"><ChevronRight size={14} /></button>
        </div>
      )}
      <div className="grid grid-cols-4 gap-1">
        {monthVersions.map((mv) => (
          <button
            key={mv.month}
            onClick={() => handleMonthClick(mv.month)}
            className="flex items-center justify-center gap-1 px-2 py-2 text-sm rounded-md transition-colors"
            style={mv.month === workingMonth
              ? { background: 'var(--lodge-orange-100)', color: 'var(--lodge-orange-600)', fontWeight: 600 }
              : { color: mv.status === 'locked' ? 'var(--lodge-onyx-400)' : 'var(--lodge-onyx-600)' }}
            onMouseEnter={e => { if (mv.month !== workingMonth) (e.currentTarget as HTMLButtonElement).style.background = 'var(--lodge-onyx-50)'; }}
            onMouseLeave={e => { if (mv.month !== workingMonth) (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
          >
            <span>{MONTH_NAMES[mv.month - 1]}</span>
            <MonthStatusIcon status={mv.status} size={10} />
          </button>
        ))}
      </div>
      <div className="mt-2 pt-2 flex items-center gap-4 text-xs" style={{ borderTop: '1px solid var(--lodge-onyx-50)', color: 'var(--lodge-onyx-400)' }}>
        <span className="flex items-center gap-1"><Lock size={10} /> Locked</span>
        <span className="flex items-center gap-1"><Edit3 size={10} style={{ color: 'var(--lodge-orange-400)' }} /> Draft</span>
        <span className="flex items-center gap-1"><TrendingUp size={10} style={{ color: 'var(--color-info)' }} /> Projected</span>
      </div>
    </div>
  );

  if (compact) {
    const statusLabel = workingMonthStatus === 'draft' ? 'DRAFT'
      : workingMonthStatus === 'locked' ? 'LOCKED'
      : workingMonthStatus === 'projected' ? 'PROJ' : null;
    const statusStyle = workingMonthStatus === 'draft'
      ? { color: 'var(--lodge-orange-600)', background: 'var(--lodge-orange-50)' }
      : workingMonthStatus === 'projected'
        ? { color: 'var(--color-info-ink)', background: 'var(--color-info-bg)' }
        : { color: 'var(--lodge-onyx-500)', background: 'var(--lodge-onyx-100)' };
    const propertyCap = pickersInShell ? SHELL_PROPERTY_MAX : propertyMaxWidth;
    const versionCap = pickersInShell ? SHELL_VERSION_MAX : versionMaxWidth;
    // In the app header the pickers give way before the breadcrumb does:
    // they shrink (truncating the name) instead of holding their full width.
    const pickerClass = pickersInShell ? 'min-w-0 shrink' : 'min-w-0';
    const propertyPicker = (
      <>
        {locations.length > 0 ? (
          <HeaderSelect
            options={locations.map(l => ({ id: l.id, label: l.name }))}
            value={selectedLocation?.id ?? null}
            onChange={handleLocationSelect}
            icon={<Building2 size={14} className="flex-none" style={{ color: 'var(--lodge-onyx-400)' }} />}
            maxLabelWidth={propertyCap}
            ariaLabel="Property"
            className={pickerClass}
          />
        ) : (
          <div className="flex items-center gap-2 h-[30px] px-2.5 rounded-lg min-w-0" style={{ background: 'var(--lodge-onyx-50)' }}>
            <Building2 size={14} className="flex-none" style={{ color: 'var(--lodge-onyx-400)' }} />
            <span className="text-[13px] font-semibold truncate" style={{ color: 'var(--lodge-onyx-800)', maxWidth: propertyCap }} title={propertyName}>{propertyName}</span>
          </div>
        )}
      </>
    );
    const versionPicker = (
      <>
        {versionOptions.length > 0 && (
          <HeaderSelect
            options={versionOptions}
            value={currentVersion?.id ?? null}
            onChange={handleVersionSelect}
            icon={<Layers size={13} className="flex-none" style={{ color: 'var(--lodge-onyx-400)' }} />}
            maxLabelWidth={versionCap}
            ariaLabel="Forecast version"
            className={pickerClass}
          />
        )}
      </>
    );
    return (
      <div ref={headerRef} className="fv3-header-compact flex items-center gap-2 pl-3 pr-2 h-[52px] flex-none whitespace-nowrap min-w-0">
        {!pickersInShell && propertyPicker}

        {/* Period chip: year / working month · status. One control for what
            used to be a year select, arrows and a month button. */}
        <div ref={periodRef} className="relative flex-none">
          <button
            onClick={() => setShowMonthPicker(!showMonthPicker)}
            className="flex items-center gap-1.5 h-[30px] px-2.5 text-[13px] rounded-lg transition-colors"
            style={showMonthPicker
              ? { background: 'var(--lodge-orange-50)', boxShadow: '0 0 0 1px var(--lodge-orange-300)' }
              : { background: 'var(--lodge-onyx-50)' }}
            aria-label="Year and month"
          >
            <span className="font-mono font-medium" style={{ color: 'var(--lodge-onyx-700)' }}>{year}</span>
            <span style={{ color: 'var(--lodge-onyx-200)' }}>/</span>
            <span className="font-semibold" style={{ color: workingMonthStatus === 'draft' ? 'var(--lodge-orange-600)' : 'var(--lodge-onyx-800)' }}>
              {MONTH_NAMES[workingMonth - 1]}
            </span>
            {statusLabel && (
              <span className="text-[10.5px] font-semibold rounded px-1.5 py-0.5" style={{ letterSpacing: '0.4px', ...statusStyle }}>{statusLabel}</span>
            )}
            <ChevronDown size={12} style={{ color: 'var(--lodge-onyx-400)' }} />
          </button>
          {monthPicker}
        </div>

        {!pickersInShell && versionPicker}

        {lockButton}

        {pickersInShell && createPortal(
          <div className="fv3-shell-pickers flex min-w-0 items-center gap-1.5">
            <span aria-hidden className="mx-1 h-5 w-px flex-none" style={{ background: 'var(--lodge-onyx-100)' }} />
            {propertyPicker}
            {versionPicker}
          </div>,
          headerSlot!
        )}

        <VersionSwitchModal
          isOpen={isVersionModalOpen}
          onClose={handleCloseModal}
          targetVersion={pendingVersion}
          onSetDefault={handleSetAsDefault}
          onViewOnly={handleViewOnly}
          isLoading={isSettingDefault}
        />
      </div>
    );
  }

  return (
    <div
      ref={headerRef}
      className="fv3-header h-14 pl-4 flex items-center justify-between gap-2 rounded-t-xl whitespace-nowrap"
      style={{ background: 'var(--lodge-white)', borderBottom: '1px solid var(--lodge-onyx-100)', paddingRight: trayGap ? 148 : 16 }}
    >
      {/* ===== LEFT: Location & Navigation =====
          The only group allowed to shrink: a truncated property name is still
          readable, a squeezed control is not. */}
      <div className={`flex items-center min-w-0 flex-1 ${tier === 'sm' ? 'gap-1.5' : 'gap-3'}`}>
        {/* Property selector — searchable: an operator with 40 properties
            should type three letters, not scroll a native option list. */}
        {locations.length > 0 ? (
          <HeaderSelect
            options={locations.map(l => ({ id: l.id, label: l.name }))}
            value={selectedLocation?.id ?? null}
            onChange={handleLocationSelect}
            icon={<Building2 size={16} className="flex-none" style={{ color: 'var(--lodge-onyx-400)' }} />}
            maxLabelWidth={propertyMaxWidth}
            ariaLabel="Property"
            className="min-w-0"
          />
        ) : (
          <div className="flex items-center gap-2 h-8 px-3 rounded-lg min-w-0" style={{ background: 'var(--lodge-onyx-50)' }}>
            <Building2 size={16} className="flex-none" style={{ color: 'var(--lodge-onyx-400)' }} />
            <span className="text-sm font-semibold truncate" style={{ color: 'var(--lodge-onyx-800)', maxWidth: propertyMaxWidth }} title={propertyName}>{propertyName}</span>
          </div>
        )}

        <HeaderDivider hidden={!showDividers || !showTypeChip} />

        {/* Budget/Forecast Type Indicator */}
        {showTypeChip && (
          <div
            className="px-3 py-1.5 text-sm font-medium rounded-lg flex-none"
            style={currentVersion?.type === 'Budget'
              ? { background: 'var(--color-info-bg)', color: 'var(--color-info-ink)', border: '1px solid rgba(11,165,236,0.35)' }
              : { background: 'var(--lodge-orange-50)', color: 'var(--lodge-orange-600)', border: '1px solid var(--lodge-orange-200)' }
            }
          >
            {currentVersion?.type === 'Budget' ? 'Budget' : 'Forecast'}
            {tier === 'xl' && currentVersion && currentVersion.year !== year ? ` · ${currentVersion.year}` : ''}
          </div>
        )}
      </div>

      {/* ===== CENTER: Time & Version Controls ===== */}
      <div className="flex items-center gap-2 flex-none">
        {/* Year selector — the arrows are a shortcut for the select next to
            them, so they are the first thing to go when space is tight. */}
        <div className="flex items-center h-8 rounded-lg overflow-hidden flex-none" style={{ background: 'var(--lodge-onyx-50)' }}>
          {showYearArrows && (
          <button
            onClick={() => handleYearNav('prev')}
            className="px-2 h-full transition-colors"
            style={{ color: 'var(--lodge-onyx-400)' }}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--lodge-onyx-700)'; (e.currentTarget as HTMLButtonElement).style.background = 'var(--lodge-onyx-100)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--lodge-onyx-400)'; (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
          >
            <ChevronLeft size={14} />
          </button>
          )}
          <div className="flex items-center gap-1.5 px-2">
            <Calendar size={14} style={{ color: 'var(--lodge-onyx-400)' }} />
            <select
              value={year}
              onChange={handleYearSelect}
              className="text-sm bg-transparent !border-0 focus:outline-none !ring-0 !shadow-none cursor-pointer font-medium appearance-none"
              style={{ color: 'var(--lodge-onyx-700)' }}
            >
              {yearOptions.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          {showYearArrows && (
          <button
            onClick={() => handleYearNav('next')}
            className="px-2 h-full transition-colors"
            style={{ color: 'var(--lodge-onyx-400)' }}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--lodge-onyx-700)'; (e.currentTarget as HTMLButtonElement).style.background = 'var(--lodge-onyx-100)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--lodge-onyx-400)'; (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
          >
            <ChevronRight size={14} />
          </button>
          )}
        </div>

        {/* Month selector */}
        <div ref={periodRef} className="relative flex-none">
          <button
            onClick={() => setShowMonthPicker(!showMonthPicker)}
            className="flex items-center gap-2 h-8 px-3 text-sm rounded-lg transition-colors flex-none"
            style={showMonthPicker
              ? { background: 'var(--lodge-orange-50)', boxShadow: '0 0 0 1px var(--lodge-orange-300)' }
              : { background: 'var(--lodge-onyx-50)' }}
            onMouseEnter={e => { if (!showMonthPicker) (e.currentTarget as HTMLButtonElement).style.background = 'var(--lodge-onyx-100)'; }}
            onMouseLeave={e => { if (!showMonthPicker) (e.currentTarget as HTMLButtonElement).style.background = 'var(--lodge-onyx-50)'; }}
          >
            <span className="font-medium" style={{ color: 'var(--lodge-onyx-700)' }}>
              {MONTH_NAMES[workingMonth - 1]}
            </span>
            <MonthStatusIcon status={workingMonthStatus} size={14} />
            <ChevronDown size={14} style={{ color: 'var(--lodge-onyx-400)' }} />
          </button>

          {/* Month picker dropdown */}
          {monthPicker}
        </div>
      </div>

      {/* ===== RIGHT: Version & Actions ===== */}
      <div className="flex items-center gap-2 flex-none">
        {/* Version selector */}
        {versionOptions.length > 0 && (
          <HeaderSelect
            options={versionOptions}
            value={currentVersion?.id ?? null}
            onChange={handleVersionSelect}
            icon={<Layers size={14} className="flex-none" style={{ color: 'var(--lodge-onyx-400)' }} />}
            maxLabelWidth={versionMaxWidth}
            ariaLabel="Forecast version"
          />
        )}

        <HeaderDivider hidden={!showDividers} />

        {/* Lock/Unlock actions. "Lock Aug" repeats the month selector two
            controls away, so the month name goes before the word "Lock" does,
            and the label goes before the button does. */}
        {currentMonthInfo?.status === 'draft' && onLockMonth && (
          <button
            onClick={onLockMonth}
            className={`flex items-center gap-1.5 h-8 text-sm font-medium rounded-lg transition-colors flex-none ${showLockLabel ? 'px-3' : 'w-8 justify-center'}`}
            style={{ color: 'var(--lodge-onyx-600)', background: 'var(--lodge-onyx-50)' }}
            onMouseEnter={e => (e.currentTarget.style.background = 'var(--lodge-onyx-100)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'var(--lodge-onyx-50)')}
            title={`Lock ${MONTH_NAMES[workingMonth - 1]}`}
          >
            <Lock size={14} className="flex-none" />
            {showLockLabel && <span>Lock{showLockMonthName ? ` ${MONTH_NAMES[workingMonth - 1]}` : ''}</span>}
          </button>
        )}

        {currentMonthInfo?.status === 'locked' && onUnlockMonth && activeMonth && (
          <button
            onClick={() => onUnlockMonth(activeMonth)}
            className={`flex items-center gap-1.5 h-8 text-sm font-medium rounded-lg transition-colors flex-none ${showLockLabel ? 'px-3' : 'w-8 justify-center'}`}
            style={{ color: 'var(--lodge-onyx-600)', background: 'var(--lodge-onyx-50)' }}
            onMouseEnter={e => (e.currentTarget.style.background = 'var(--lodge-onyx-100)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'var(--lodge-onyx-50)')}
            title={`Unlock ${MONTH_NAMES[activeMonth - 1]}`}
          >
            <LockOpen size={14} className="flex-none" />
            {showLockLabel && <span>Unlock{showLockMonthName ? ` ${MONTH_NAMES[activeMonth - 1]}` : ''}</span>}
          </button>
        )}

        {/* Status pill */}
        {currentVersion && showStatusPill && <StatusPill status={currentVersion.status} />}
      </div>

      {/* Version Switch Modal */}
      <VersionSwitchModal
        isOpen={isVersionModalOpen}
        onClose={handleCloseModal}
        targetVersion={pendingVersion}
        onSetDefault={handleSetAsDefault}
        onViewOnly={handleViewOnly}
        isLoading={isSettingDefault}
      />
    </div>
  );
}

export default ForecastHeader;
